<template>
  <UApp
    :toaster="{
      position: 'top-center',
      expand: false
    }"
  >
    <div
      v-if="!showIframe && companyNotFound"
      class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white"
    >
      <div class="flex max-w-[600px] flex-col items-center justify-center px-[40px] text-center">
        <div class="text-[40px] font-bold text-gray-800">{{ t('app.companyNotFoundTitle') }}</div>
        <div class="mt-[20px] text-[28px] leading-[1.6] text-gray-500">
          {{ t('app.companyNotFoundMessage') }}
        </div>
      </div>
    </div>

    <!-- 陪练业务未开通提醒 -->
    <div 
      v-else-if="!showIframe && showPracticeDisabledWarning" 
      class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white"
    >
      <div class="flex flex-col items-center justify-center max-w-[600px] px-[40px]">
        <div class="text-[48px] mb-[20px]">⚠️</div>
        <div class="text-[38px] font-bold text-gray-800 mb-[16px] text-center">{{ t('app.practiceDisabledTitle') }}</div>
        <div class="text-[28px] text-gray-600 text-center">
          {{ t('app.practiceDisabledMessage', { company: companyData?.name || t('common.currentCompany') }) }}
        </div>
      </div>
    </div>

    <!-- 全局loading -->
    <div 
      v-else-if="!showIframe && isLoading" 
      class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white bg-opacity-90"
    >
      <UIcon name="i-custom-ai-loading" class="w-[80px] h-[80px] text-(--ui-primary) animate-spin"/>
      <div class="mt-[40px] text-[32px] text-gray-700">{{ loadingText }}</div>
    </div>

    <div v-if="showIframe" class="pc-iframe-wrapper">
      <ClientOnly>
        <iframe
          :src="currentUrl"
          class="pc-iframe"
          frameborder="0"
          scrolling="auto"
        />
      </ClientOnly>
    </div>

    <template v-else>
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
    </template>
  </UApp>
</template>


<script setup lang="ts">
const { fetchCompanyData, companyData, companyNotFound } = useCompany();
const { jwtError, verifyTokenFromURL } = useJWT();
const { verifyEvaluation } = useEvaluation();
const { initAuth } = usePracticeUser();
const { initLocale, t } = useAppI18n();
const { captureException, trackEvent, trackLocaleDiagnostics, syncScope } = useMonitoring();
const router = useRouter();
const toast = useToast();

// 全局loading状态
const isLoading = ref(false);
const loadingText = ref(t('app.loadingApp'));

// 陪练业务未开通警告
const showPracticeDisabledWarning = computed(() => {
  return companyData.value && companyData.value.practice_enabled === false;
});

// iframe相关状态
const showIframe = ref(false);
const currentUrl = ref('');

// 检查屏幕宽度并更新iframe状态
const checkWidth = () => {
  showIframe.value = window.innerWidth > 600;
};

// 更新当前URL
const updateCurrentUrl = () => {
  currentUrl.value = window.location.href;
};

// 处理来自iframe的消息
const handleIframeMessage = (event: MessageEvent) => {
  // 安全检查：可以根据需要添加 origin 验证
  if (event.data && event.data.type === 'redirect' && event.data.url) {
    window.location.href = event.data.url;
  }
};

// 监听屏幕宽度变化和路由变化
onMounted(() => {
  initLocale();
  trackLocaleDiagnostics('app_mounted');
  checkWidth();
  updateCurrentUrl();
  syncScope();
  const urlParams = new URLSearchParams(window.location.search);
  const hasToken = Boolean(urlParams.get('token'));

  trackEvent('app_mounted', {
    path: window.location.pathname,
    has_token: hasToken,
  });

  if (urlParams.get('glitchtip_test_error') === '1') {
    trackEvent('glitchtip_test_error_triggered', {
      path: window.location.pathname,
    });

    setTimeout(() => {
      captureException(new Error('[GlitchTip Test] Frontend test error'), {
        tags: {
          module: 'glitchtip_test',
          error_kind: 'manual_verification',
        },
        context: {
          path: window.location.pathname,
          search: window.location.search,
        },
      });
    }, 0);
  }
  window.addEventListener('resize', checkWidth);
  window.addEventListener('popstate', updateCurrentUrl);
  // 监听来自iframe的消息
  window.addEventListener('message', handleIframeMessage);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkWidth);
  window.removeEventListener('popstate', updateCurrentUrl);
});

// JWT模式自动验证并跳转
const handleSJKAutoVerification = async (payload: { batch_id: string; sub: string; callback_url?: string; user_name?: string }) => {
  try {
    loadingText.value = t('app.verifyingEligibility');
    
    // 调用SJK渠道的评测验证
    const response = await verifyEvaluation({
      company_id: companyData.value?.id,
      batch_id: payload.batch_id,
      sub: payload.sub,
      call_url: payload.callback_url,
      user_name: payload.user_name
    });
    
    if (response.eligible) {
      // 存储评测信息到localStorage
      localStorage.setItem('evaluation_id', response.evaluation_id?.toString() || '');
      localStorage.setItem('thread_id', response.thread_id?.toString() || '');
      localStorage.setItem('employee_name', payload.sub);
      localStorage.setItem('employee_code', payload.sub);
      localStorage.setItem('batch_id', payload.batch_id.toString());
      localStorage.setItem('company_id', companyData.value?.id?.toString() || '');
      localStorage.setItem('sub', payload.sub);
      localStorage.setItem('callback_url', payload.callback_url || '');
      localStorage.setItem('is_sjk_channel', 'true');
      localStorage.setItem('user_name', payload.user_name || '');
      
      // 根据状态跳转到不同页面
      if (response.status === 'waiting') {
        // 首次评测，跳转到准备页面
        router.replace('/prepare');
      } else if (response.status === 'ongoing') {
        router.replace('/prepare');
      } else if (response.status === 'completed') {
        // 已完成的评测，跳转到结果页面
        router.replace(`/end`);
      } else {
        toast.add({
          title: response.message || t('app.evaluationStatusError'),
          type: 'foreground',
          duration: 3000
        });
      }
    } else {
      console.error('SJK验证失败:', response.message);
      trackEvent('sjk_verification_failed', {
        company_id: companyData.value?.id,
        message: response.message || t('app.verifyFailed'),
      });
      // 跳转到权限无效页面并传递错误信息
      router.replace(`/unauthorized?message=${encodeURIComponent(response.message || t('app.verifyFailed'))}`);
    }
  } catch (error) {
    console.error('SJK自动验证失败:', error);
    captureException(error, {
      tags: {
        module: 'sjk_verification',
      },
      context: {
        company_id: companyData.value?.id,
        batch_id: payload.batch_id,
        sub: payload.sub,
      },
    });
    // 跳转到权限无效页面并传递错误信息
    router.replace(`/unauthorized?message=${encodeURIComponent(t('app.verifyFailedRetry'))}`);
  }
};

// 在应用启动时处理JWT验证和获取公司数据
onMounted(async () => {
  // 检查是否为客户端环境
  if (typeof window === 'undefined') return;
  
  // 检查是否存在JWT token
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  
  if (token) {
    try {
      isLoading.value = true;
      loadingText.value = t('app.loadingCompanyInfo');
      
      // 先获取公司数据
      const company = await fetchCompanyData();
      if (!company) return;
      
      loadingText.value = t('app.verifyingIdentity');
      const payload = await verifyTokenFromURL();
      
      if (payload) {
        // JWT验证成功，直接进行SJK验证
        trackEvent('jwt_verification_succeeded', {
          batch_id: payload.batch_id,
          sub: payload.sub,
        });
        await handleSJKAutoVerification(payload);
      } else {
        // JWT验证失败，跳转到权限无效页面
        console.error('JWT验证失败:', jwtError.value);
        trackEvent('jwt_verification_failed', {
          message: jwtError.value || t('app.jwtInvalid'),
        });
        router.replace(`/unauthorized?message=${encodeURIComponent(jwtError.value || t('app.jwtInvalid'))}`);
      }
    } catch (error) {
      console.error('JWT验证异常:', error);
      captureException(error, {
        tags: {
          module: 'jwt_verification',
        },
      });
      router.replace(`/unauthorized?message=${encodeURIComponent(jwtError.value || t('app.jwtInvalid'))}`);
    } finally {
      isLoading.value = false;
    }
  } else {
    // 非JWT模式，获取公司数据并验证陪练账号登录状态
    const company = await fetchCompanyData();
    if (!company) return;
    
    // 验证陪练账号的token（如果存在）
    if (import.meta.client) {
      try {
        await initAuth();
      } catch (error) {
        console.error('验证陪练账号token失败:', error);
        captureException(error, {
          tags: {
            module: 'practice_auth',
          },
        });
      }
    }
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', checkWidth);
  window.removeEventListener('popstate', updateCurrentUrl);
  // 移除消息监听器
  window.removeEventListener('message', handleIframeMessage);
});


</script>


<style>
body {
  width: 804px;
}
/* 确保 UModal 在 overlay 中居中显示 */
.custom-UModal {
  margin: auto;
}

.page-enter-active,
.page-leave-active {
  transition: all 0.4s;
}
.page-enter-from,
.page-leave-to {
  opacity: 0;
  filter: blur(1rem);
}

.pc-iframe-wrapper {
  position: fixed;
  inset: 0;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.pc-iframe {
  max-height: calc(100vh - 40px);
  max-width: calc(100vw - 40px);
  aspect-ratio: 430 / 932;
  width: auto;
  height: auto;
  border-radius: 20px;
  box-shadow: 0 0 40px rgba(0,0,0,0.15);
  background: #fff;
  display: block;
}
</style>
